// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico']);

const OFFICE_EXT = new Set(['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv']);

/** Match image extension in path or query (signed URLs often omit a clean path ending). */
const IMAGE_IN_URL_RE = /\.(jpe?g|png|gif|webp|svg|bmp|ico)(?=$|[?#&/])/i;

const OFFICE_IN_URL_RE = /\.(docx?|xlsx?|pptx?|csv)(?=$|[?#&/])/i;

/**
 * True when the file should use the in-app preview modal (images + PDF only).
 * Word, Excel, CSV, etc. open in a new tab so the browser can use session/auth.
 */
export function shouldOpenFileInPreviewModal(fileName, fileUrl) {
  const k = getPreviewKind(fileName, fileUrl);
  return k === 'image' || k === 'pdf';
}

/**
 * Classify how to show a file in preview from filename and/or URL path (extension).
 */
export function getPreviewKind(fileName, fileUrl) {
  let ext = '';
  if (fileName && String(fileName).includes('.')) {
    ext = String(fileName)
      .trim()
      .split('.')
      .pop()
      .toLowerCase();
  }
  if (ext === 'jpeg') {
    ext = 'jpg';
  }
  if (!ext && fileUrl) {
    const all = String(fileUrl);
    const m = all.match(IMAGE_IN_URL_RE);
    if (m) {
      ext = m[1].toLowerCase();
      if (ext === 'jpeg') {
        ext = 'jpg';
      }
    }
  }
  if (!ext && fileUrl) {
    try {
      const path = new URL(fileUrl, window.location.origin).pathname;
      const parts = path.split('.');
      if (parts.length > 1) {
        ext = parts.pop().toLowerCase();
      }
    } catch {
      // ignore
    }
  }
  if (ext && IMAGE_EXT.has(ext)) {
    return 'image';
  }
  if (ext === 'pdf') {
    return 'pdf';
  }
  if (ext && OFFICE_EXT.has(ext)) {
    return 'office';
  }
  if (fileName && OFFICE_IN_URL_RE.test(String(fileName))) {
    return 'office';
  }
  if (fileUrl && OFFICE_IN_URL_RE.test(String(fileUrl))) {
    return 'office';
  }
  if (fileName && IMAGE_IN_URL_RE.test(String(fileName))) {
    return 'image';
  }
  if (fileUrl && IMAGE_IN_URL_RE.test(String(fileUrl))) {
    return 'image';
  }
  return 'download';
}

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 3;

/**
 * `kind` (optional, ACOP-123): the caller already knows what the file is. Record files arrive as
 * blob: URLs, which carry no extension to classify, and their kind comes from the server's
 * Content-Type allowlist. For such a blob the img → direct → iframe fallbacks are skipped (an iframe
 * would render the blob as a document in Copilot's own origin) and "Open in new tab" is hidden.
 */
export default function FilePreviewModal({ isOpen, fileUrl, fileName, onClose, kind = undefined }) {
  const [imageScale, setImageScale] = useState(1);
  const [loadFailedFinal, setLoadFailedFinal] = useState(false);
  /** none | direct | blob-then-direct | iframe | own-blob */
  const [imageTactic, setImageTactic] = useState('none');
  const [imagePhase, setImagePhase] = useState('idle');
  const [blobOrDirectSrc, setBlobOrDirectSrc] = useState(null);
  const blobRef = useRef(null);

  // Computed once per render and used by the load effect and every guard below (UI-3).
  const baseKind = kind || getPreviewKind(fileName, fileUrl);
  const isOwnBlob = !!kind && typeof fileUrl === 'string' && /^blob:/i.test(fileUrl);
  const isImageKind = baseKind === 'image';
  const showImageErrorPanel = isImageKind && loadFailedFinal;
  const showDownloadTypePanel = !isImageKind && baseKind === 'download';

  const showImageInIframe = isImageKind && imageTactic === 'iframe' && !loadFailedFinal;
  const showImageAsImg = isImageKind && blobOrDirectSrc && !showImageInIframe && !loadFailedFinal;

  const clearBlob = useCallback(() => {
    if (blobRef.current) {
      try {
        URL.revokeObjectURL(blobRef.current);
      } catch {
        // ignore
      }
      blobRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !fileUrl || baseKind !== 'image') {
      setImagePhase('n/a');
      setBlobOrDirectSrc(null);
      setImageTactic('none');
      setLoadFailedFinal(false);
      clearBlob();
      return;
    }

    if (isOwnBlob) {
      // The caller owns (and later revokes) this object URL; show it as-is, with no copy of our own.
      clearBlob();
      setLoadFailedFinal(false);
      setBlobOrDirectSrc(fileUrl);
      setImageTactic('own-blob');
      setImagePhase('ready');
      return;
    }

    let cancelled = false;
    setLoadFailedFinal(false);
    setImageTactic('none');
    setBlobOrDirectSrc(null);
    setImagePhase('loading');
    clearBlob();

    (async () => {
      for (const creds of ['omit', 'include']) {
        if (cancelled) {
          return;
        }
        try {
          const res = await fetch(fileUrl, { method: 'GET', mode: 'cors', credentials: creds });
          if (cancelled || !res.ok) {
            continue;
          }
          const b = await res.blob();
          if (cancelled || !b || b.size < 1) {
            continue;
          }
          if (cancelled) {
            return;
          }
          const u = URL.createObjectURL(b);
          if (cancelled) {
            URL.revokeObjectURL(u);
            return;
          }
          blobRef.current = u;
          setBlobOrDirectSrc(u);
          setImageTactic('blob');
          setImagePhase('ready');
          return;
        } catch {
          // CORS, etc.
        }
      }
      if (cancelled) {
        return;
      }
      setBlobOrDirectSrc(fileUrl);
      setImageTactic('direct');
      setImagePhase('ready');
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, fileUrl, baseKind, isOwnBlob, clearBlob]);

  useEffect(() => {
    return () => {
      if (blobRef.current) {
        try {
          URL.revokeObjectURL(blobRef.current);
        } catch {
          // ignore
        }
        blobRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setImageScale(1);
    }
  }, [isOpen, fileUrl]);

  const onImgError = useCallback(() => {
    if (imageTactic === 'own-blob') {
      setLoadFailedFinal(true);
      return;
    }
    if (imageTactic === 'blob') {
      clearBlob();
      setBlobOrDirectSrc(fileUrl);
      setImageTactic('direct');
      return;
    }
    if (imageTactic === 'direct') {
      setImageTactic('iframe');
      return;
    }
    if (imageTactic === 'iframe') {
      setLoadFailedFinal(true);
    }
  }, [imageTactic, fileUrl, clearBlob]);

  const onWheel = useCallback(
    (e) => {
      if (!isImageKind || showImageInIframe) {
        return;
      }
      e.preventDefault();
      setImageScale((s) => {
        const next = s - e.deltaY * 0.0015;
        return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
      });
    },
    [isImageKind, showImageInIframe]
  );

  if (!isOpen || !fileUrl) {
    return null;
  }

  const title = fileName && String(fileName).trim() ? fileName : 'File preview';
  const imageHeaderTools = isImageKind && !loadFailedFinal && !showImageInIframe;

  return (
    <>
      <div
        className="modal fade show overflow-hidden copilot-file-preview-modal"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="copilotFilePreviewTitle"
        data-testid="file-preview-modal"
        style={{ display: 'block', zIndex: 2060 }}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      >
        <div className="modal-dialog modal-fullscreen-md-down" style={{ maxWidth: 'min(96vw, 1200px)' }}>
          <div className="modal-content">
            <div className="modal-header align-items-center flex-wrap gap-2">
              <h5 className="modal-title text-truncate me-auto" id="copilotFilePreviewTitle" title={String(title)}>
                {title}
              </h5>
              <div className="d-flex align-items-center flex-wrap gap-2 flex-shrink-0">
                <a
                  className="btn btn-sm btn-outline-primary"
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={fileName || undefined}
                >
                  Download
                </a>
                {!isOwnBlob && (
                  <a className="btn btn-sm btn-outline-secondary" href={fileUrl} target="_blank" rel="noopener noreferrer">
                    Open in new tab
                  </a>
                )}
                {imageHeaderTools && (
                  <>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setImageScale((s) => Math.min(MAX_ZOOM, s * 1.2))}
                    >
                      Zoom in
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setImageScale((s) => Math.max(MIN_ZOOM, s / 1.2))}
                    >
                      Zoom out
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setImageScale(1)}>
                      Reset
                    </button>
                    <span className="small text-muted d-none d-sm-inline">{(imageScale * 100).toFixed(0)}%</span>
                  </>
                )}
                {showImageInIframe && (
                  <span className="small text-muted d-none d-sm-inline">Scroll the preview, or use Open in new tab</span>
                )}
                <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
              </div>
            </div>
            <div className="modal-body p-0 bg-body-secondary">
              <div className="copilot-file-preview__outer p-2 p-md-3">
                <div className="copilot-file-preview__stage">
                  {isImageKind && !loadFailedFinal && imagePhase === 'loading' && (
                    <div className="copilot-file-preview__stage-fallback d-flex flex-column align-items-center justify-content-center w-100 h-100 p-4">
                      <div className="spinner-border text-primary mb-2" role="status" aria-label="Loading" />
                      <span className="text-muted small">Loading preview…</span>
                    </div>
                  )}

                  {isImageKind && !loadFailedFinal && imagePhase === 'ready' && showImageAsImg && (
                    <div
                      className="copilot-file-preview__stage-body copilot-file-preview__stage-body--image overflow-auto"
                      onWheel={onWheel}
                    >
                      <div className="copilot-file-preview__stage-image-wrap d-flex align-items-center justify-content-center h-100 w-100 p-1">
                        <img
                          key={blobOrDirectSrc}
                          src={blobOrDirectSrc}
                          alt={fileName || 'Attachment'}
                          className="copilot-file-preview__image"
                          onError={onImgError}
                          decoding="async"
                          style={{
                            maxWidth: `${Math.min(500, 100 * imageScale)}%`,
                            maxHeight: `${Math.min(500, 100 * imageScale)}%`,
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {isImageKind && !loadFailedFinal && imagePhase === 'ready' && showImageInIframe && (
                    <iframe
                      key="iframe"
                      src={fileUrl}
                      title={String(title)}
                      className="copilot-file-preview__stage-iframe copilot-file-preview__stage-iframe--image w-100 border-0"
                    />
                  )}

                  {showImageErrorPanel && (
                    <div className="copilot-file-preview__stage-fallback d-flex flex-column align-items-center justify-content-center text-center p-3 p-md-4 h-100 w-100">
                      <p className="text-muted mb-3 mb-md-4 small">
                        Preview is not available for this file in Copilot, or the file could not be loaded in the browser.
                        {isOwnBlob ? (
                          <> Try <strong>Download</strong>.</>
                        ) : (
                          <> Try <strong>Download</strong> or <strong>Open in new tab</strong>.</>
                        )}
                      </p>
                      <div className="d-flex flex-wrap gap-2 justify-content-center">
                        <a
                          className="btn btn-primary"
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={fileName || undefined}
                        >
                          Download file
                        </a>
                        {!isOwnBlob && (
                          <a className="btn btn-outline-primary" href={fileUrl} target="_blank" rel="noopener noreferrer">
                            Open in new tab
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {baseKind === 'pdf' && (
                    <iframe src={fileUrl} title={String(title)} className="copilot-file-preview__stage-iframe w-100 border-0" />
                  )}

                  {showDownloadTypePanel && (
                    <div className="copilot-file-preview__stage-fallback d-flex flex-column align-items-center justify-content-center text-center p-3 p-md-4 h-100 w-100">
                      <p className="text-muted mb-3 mb-md-4 small">
                        Preview is not available for this file type in Copilot, or the file could not be loaded in the
                        browser.
                      </p>
                      <div className="d-flex flex-wrap gap-2 justify-content-center">
                        <a
                          className="btn btn-primary"
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={fileName || undefined}
                        >
                          Download file
                        </a>
                        {!isOwnBlob && (
                          <a className="btn btn-outline-primary" href={fileUrl} target="_blank" rel="noopener noreferrer">
                            Open in new tab
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 2050 }} />
    </>
  );
}
